import zodToJsonSchema from 'zod-to-json-schema';
import { tool, jsonSchema } from 'ai';
import { wrapTraced } from 'braintrust';
import { z } from 'zod';

function makeServerTool(tool) {
  return tool;
}
function makeClientTool(tool) {
  return tool;
}
function makeFinalTool(tool) {
  return tool;
}
function makeToolComponent(component) {
  return component;
}

function extractComponents(tools) {
  return Object.fromEntries(
    tools.filter(
      (tool) => tool.component !== void 0
    ).map((tool) => [tool.name, tool.component])
  );
}
function serializeTools(tools) {
  return tools.map(({ name, description, parameters }) => ({
    name,
    description,
    parameters: zodToJsonSchema(parameters)
  }));
}
function createOnToolCall(tools) {
  const execMap = Object.fromEntries(tools.map((t) => [t.name, t.execute]));
  return async (params) => {
    if (!params) {
      console.warn(
        "createOnToolCall: params is undefined.\n\nDid you setState(createOnToolCall(...))? If so, use setState(() => createOnToolCall(...)) instead."
      );
      return Promise.resolve(void 0);
    }
    const { toolCall } = params;
    const { toolName, args } = toolCall;
    const handler = execMap[toolName];
    if (!handler) return Promise.resolve(void 0);
    return handler(args);
  };
}

async function createServerTools({
  serverTools = [],
  clientTools = [],
  disableErrorWrapping = false,
  parentSpanId,
  logger
}) {
  const tools = {};
  serverTools.forEach((t) => {
    tools[t.name] = "execute" in t ? tool({
      description: t.description,
      parameters: disableErrorWrapping ? t.parameters : createValidationBypassSchema(t.parameters),
      execute: (() => {
        let wrappedExecute = t.execute;
        if (!disableErrorWrapping) {
          wrappedExecute = wrapExecuteWithValidation(
            wrappedExecute,
            t.parameters
          );
        }
        if (logger) {
          wrappedExecute = wrapExecuteWithTracing(
            wrappedExecute,
            t.name,
            parentSpanId,
            logger
          );
        }
        return wrappedExecute;
      })()
    }) : tool({
      description: t.description,
      parameters: t.parameters
    });
  });
  clientTools.forEach((t) => {
    tools[t.name] = tool({
      description: t.description,
      parameters: jsonSchema(t.parameters)
    });
  });
  return tools;
}
function createErrorObject(error) {
  const isDevelopment = process.env.NODE_ENV === "development";
  if (error instanceof z.ZodError) {
    return {
      $error: {
        message: "Validation failed",
        issues: error.errors.map((issue) => ({
          path: issue.path.join("."),
          message: issue.message
        })),
        ...isDevelopment && { stack: error.stack }
      }
    };
  }
  if (error instanceof Error) {
    return {
      $error: {
        message: error.message,
        ...isDevelopment && { stack: error.stack }
      }
    };
  }
  return {
    $error: {
      message: typeof error === "string" ? error : "Unknown error occurred"
    }
  };
}
function createValidationBypassSchema(schema) {
  const bypass = Object.create(schema);
  bypass.parse = (data) => data;
  bypass.safeParse = (data) => ({ success: true, data });
  bypass.parseAsync = async (data) => data;
  bypass.safeParseAsync = async (data) => ({ success: true, data });
  return bypass;
}
function wrapExecuteWithTracing(execute, toolName, parentSpanId, logger) {
  if (!logger) {
    return execute;
  }
  return wrapTraced(
    async (args) => {
      const result = await execute(args);
      return result;
    },
    {
      type: "tool",
      name: `Server Tool: ${toolName}`,
      ...parentSpanId && { parent: parentSpanId }
    }
  );
}
function wrapExecuteWithValidation(execute, schema) {
  return async (args) => {
    try {
      const validatedArgs = schema.parse(args);
      return await execute(validatedArgs);
    } catch (error) {
      return createErrorObject(error);
    }
  };
}

function generateHandoffContext(copilots) {
  const copilotList = copilots.map((copilot) => {
    const paramInfo = copilot.parameters ? ` (requires parameters)` : "";
    return `- ${copilot.id}: ${copilot.description}${paramInfo}`;
  }).join("\n");
  return `Available copilots for handoff:
${copilotList}`;
}
function createCopilotRegistry(copilots) {
  const registry = {
    copilots,
    getById(id) {
      return copilots.find((c) => c.id === id);
    },
    createIdSchema() {
      const ids = copilots.map((c) => c.id);
      if (ids.length === 0) {
        throw new Error("Cannot create schema from empty registry");
      }
      const enumValues = ids;
      const description = `The ID of the copilot:

${copilots.map((c) => `* ${c.id}: ${c.name} (${c.description})`).join("\n")}`;
      return z.enum(enumValues).describe(description);
    },
    generateSystemPrompt(options) {
      const { basePrompt } = options;
      const handoffContext = generateHandoffContext(copilots);
      return basePrompt.includes("{{handoffPrompt}}") ? basePrompt.replace("{{handoffPrompt}}", handoffContext) : `${basePrompt}

${handoffContext}`;
    }
  };
  return registry;
}

function getZapierDomain() {
  const isStaging = process.env.VERCEL_ENV === "preview";
  return isStaging ? "zapier-staging.com" : "zapier.com";
}
function getZapierBaseUrl() {
  return process.env.NEXT_PUBLIC_ZAPIER_ORIGIN || `https://${getZapierDomain()}`;
}
function getAgentsBaseUrl() {
  if (process.env.NEXT_PUBLIC_AGENTS_URL) {
    return process.env.NEXT_PUBLIC_AGENTS_URL;
  }
  return `https://agents.${getZapierDomain()}`;
}
function getCanvasBaseUrl() {
  if (process.env.NEXT_PUBLIC_CANVAS_URL) {
    return process.env.NEXT_PUBLIC_CANVAS_URL;
  }
  return getZapierBaseUrl();
}
function getChatbotsBaseUrl() {
  if (process.env.NEXT_PUBLIC_CHATBOTS_URL) {
    return process.env.NEXT_PUBLIC_CHATBOTS_URL;
  }
  return getZapierBaseUrl();
}
function getEditorBaseUrl() {
  if (process.env.NEXT_PUBLIC_EDITOR_URL) {
    return process.env.NEXT_PUBLIC_EDITOR_URL;
  }
  return getZapierBaseUrl();
}
function getInterfacesBaseUrl() {
  if (process.env.NEXT_PUBLIC_INTERFACES_URL) {
    return process.env.NEXT_PUBLIC_INTERFACES_URL;
  }
  return `https://interfaces.${getZapierDomain()}`;
}
function getTablesBaseUrl() {
  if (process.env.NEXT_PUBLIC_TABLES_URL) {
    return process.env.NEXT_PUBLIC_TABLES_URL;
  }
  return `https://tables.${getZapierDomain()}`;
}
function getZapierOrigin() {
  return process.env.NEXT_PUBLIC_ZAPIER_ORIGIN || process.env.ZAPIER_ORIGIN || `https://${getZapierDomain()}`;
}
function getSemanticSearchOrigin() {
  return process.env.SEMANTIC_SEARCH_ORIGIN || "https://semanticsearch.zapier.com";
}
function getCustomActionsOrigin() {
  return process.env.CUSTOM_ACTIONS_ORIGIN || `${getZapierBaseUrl()}/app/extensions`;
}

const globalCopilots = [
  {
    id: "agents",
    name: "Agent Builder",
    description: "Create and edit Zapier Agents - AI-powered agents that can perform actions independently across your connected apps and services.",
    parameters: z.object({
      botId: z.string().optional().describe("The ID of the Agent to edit, blank if new Agent")
    }),
    generateUrl({ botId }) {
      const baseUrl = getAgentsBaseUrl();
      if (botId) {
        return `${baseUrl}/bots/${botId}/settings?tab=chat`;
      } else {
        return `${baseUrl}/bots?new=true`;
      }
    }
  },
  {
    id: "canvas",
    name: "Canvas Workflow Builder",
    description: "Visual workflow builder for creating and editing Zapier automations",
    parameters: z.object({
      canvasId: z.string().optional().describe("The ID of the Canvas to edit, blank if new Canvas")
    }),
    generateUrl: ({ canvasId }) => {
      const baseUrl = getCanvasBaseUrl();
      const canvasSuffix = canvasId ? `/app/canvas/${canvasId}` : "/app/canvas/new";
      return `${baseUrl}${canvasSuffix}`;
    }
  },
  {
    id: "editor",
    name: "Zap Editor",
    description: "Create and edit Zaps in the Zap Editor -  an intuitive, no-code platform for building workflow automations with Zapier.",
    parameters: z.object({
      zapId: z.string().optional().describe(
        "The ID of the Zap, blank if creating new Zap. Fill in the ID of the Zap you want to edit, if known"
      ),
      stepId: z.string().optional().describe(
        "The ID of the step to route to (unused if Zap ID is not provided)"
      )
    }),
    generateUrl: ({ zapId, stepId }) => {
      const baseUrl = getEditorBaseUrl();
      if (zapId) {
        if (stepId) {
          return `${baseUrl}/editor/${zapId}/draft/${stepId}`;
        }
        return `${baseUrl}/editor/${zapId}/draft`;
      }
      return `${baseUrl}/webintent/create-zap`;
    }
  },
  {
    id: "interfaces",
    name: "Interface Builder",
    /**
     * Created by prompting Copilot in Interfaces the following:
     * "Imagine you are tasked with creating a description for Interfaces for other LLMs to consume. What description you would come up with? Keep it brief, focus on functionality. Use the search help docs rather than the tools you have available."
     */
    description: `
Zapier Interfaces is a no-code platform for building custom web pages and interactive applications.
It enables users to create multi-page websites and apps using drag-and-drop components including forms, tables, kanban boards, media blocks, buttons, dividers, and embedded content.
Interfaces integrates seamlessly with Zapier Tables for data storage and Zaps for workflow automation.

Key capabilities include:
- Interactive Components: Forms for data collection, tables for data display, kanban boards for project management, media blocks, buttons, and embedded content
- Multi-page Architecture: Create websites with multiple interconnected pages and customizable navigation
- Data Integration: Connect to Zapier Tables for database functionality and trigger Zaps for automated workflows
- Access Control: Configure public access, password protection, or user-specific permissions with various authentication methods
- Customization: Apply custom branding, themes, logos, and domain names
- Automation: Trigger workflows when users interact with components like form submissions or button clicks

Interfaces serves use cases like lead capture forms, client portals, employee onboarding sites, and custom business applications, providing a bridge between data collection, presentation, and automated processing.
`,
    parameters: z.discriminatedUnion("type", [
      z.object({
        type: z.literal("new-project")
      }),
      z.object({
        type: z.literal("project"),
        projectId: z.string().cuid().describe(
          "The unique identifier (CUID) of the Zapier Interface to navigate to."
        )
      }),
      z.object({
        type: z.literal("page"),
        pageId: z.string().cuid().describe("The unique identifier (CUID) of the page to navigate to.")
      })
    ]),
    generateUrl(params) {
      const baseUrl = getInterfacesBaseUrl();
      switch (params.type) {
        case "new-project": {
          const url = new URL("/interfaces/get-started", baseUrl);
          return url.toString();
        }
        case "project": {
          const url = new URL(
            `/interfaces/project/${params.projectId}`,
            baseUrl
          );
          url.searchParams.set("sideNavDrawer", "settings-copilot");
          return url.toString();
        }
        case "page": {
          const url = new URL(`/interfaces/page/${params.pageId}`, baseUrl);
          url.searchParams.set("sideNavDrawer", "settings-copilot");
          return url.toString();
        }
        default: {
          throw new Error("Invalid type");
        }
      }
    }
  },
  {
    id: "tables",
    name: "Table Builder",
    description: `Create and manage Zapier Tables - a flexible, no-code database that lets you store, organize, and
      automate data. Built to seamlessly integrate with your workflows across Zapier`,
    parameters: z.object({
      tableId: z.string().describe("The ID of the Table to manage.")
    }),
    generateUrl({ tableId }) {
      const baseUrl = getTablesBaseUrl();
      const tablesSuffix = `/app/tables/t/${tableId}`;
      return `${baseUrl}${tablesSuffix}`;
    }
  },
  {
    id: "memberhome",
    name: "Home Page",
    description: `
      Home is your personalized starting point for automation. Find relevant use cases, tools, and features tailored to your role and interests.
      What it does:
      - Shows use cases and tools based on your needs
      - Recommends next steps to get started or go deeper
      - Easily search and browse functionality
      When to use it:
      - You're new and want to explore what's possible
      - You're returning and want to pick up where you left off
      - You want inspiration or examples from other users
    `,
    generateUrl: () => {
      const baseUrl = getZapierBaseUrl();
      return `${baseUrl}/app/home`;
    }
  },
  {
    id: "chatbots",
    name: "Chatbots",
    description: `Chatbots is the easiest way to automate any customer interaction, helping your business scale support and generate more leads.`,
    parameters: z.object({
      chatbotId: z.string().optional().describe(
        "The ID of the Chatbot to edit, blank if creating new Chatbot"
      )
    }),
    generateUrl({ chatbotId }) {
      const baseUrl = getChatbotsBaseUrl();
      const path = chatbotId ? `/app/chatbots/${chatbotId}` : `/app/chatbots/create`;
      const url = new URL(path, baseUrl);
      return url.toString();
    }
  }
];
async function getGlobalCopilotRegistry() {
  return createCopilotRegistry(globalCopilots);
}

var HandoffStatus = /* @__PURE__ */ ((HandoffStatus2) => {
  HandoffStatus2["Initializing"] = "initializing";
  HandoffStatus2["None"] = "none";
  HandoffStatus2["Loading"] = "loading";
  HandoffStatus2["Ready"] = "ready";
  HandoffStatus2["Applied"] = "applied";
  HandoffStatus2["Error"] = "error";
  return HandoffStatus2;
})(HandoffStatus || {});
const HANDOFF_TOOL_NAME = "handoff";

const commonToolParams = z.object({
  displayMessage: z.string().describe(
    "A 8-12 word message to display to the user about why we are using this tool (e.g., 'Searching for relevant apps...', 'Let's take a look at your current workflows...')"
  )
});

var dist = {};

var VisitorTracking = {};

var EventsToolkitSettings = {};

var hasRequiredEventsToolkitSettings;

function requireEventsToolkitSettings () {
	if (hasRequiredEventsToolkitSettings) return EventsToolkitSettings;
	hasRequiredEventsToolkitSettings = 1;
	Object.defineProperty(EventsToolkitSettings, "__esModule", { value: true });
	EventsToolkitSettings.settings = void 0;
	function getEventsToolkitEmitAPI() {
	    var _a, _b;
	    if (process.env.NEXT_PUBLIC_EVENTS_TOOLKIT_EMIT_API) {
	        // If we're running in a browser, we'll want to check for NEXT_PUBLIC_ variables
	        // https://nextjs.org/docs/basic-features/environment-variables#exposing-environment-variables-to-the-browser
	        return process.env.NEXT_PUBLIC_EVENTS_TOOLKIT_EMIT_API;
	    }
	    else if (process.env.EVENTS_TOOLKIT_EMIT_API) {
	        // picking up the standard emit API configuration setting
	        return process.env.EVENTS_TOOLKIT_EMIT_API;
	    }
	    else {
	        // if nothing is set up, we'll default to localhost or staging emitting, depends on the environment
	        // to ensure we do our best to capture the event data
	        // Sometimes we run this toolkit in non browser environment like CI
	        if (typeof window === 'undefined') {
	            console.warn('It looks like Toolkit is running in a non-browser environment. Event data may be incomplete or inaccurate. Redirecting to staging endpoint: https://zapier-staging.com/api/v4/tracking/event/"');
	            return "https://zapier-staging.com/api/v4/tracking/event/";
	        }
	        const isLocalhost = (((_a = window === null || window === void 0 ? void 0 : window.location) === null || _a === void 0 ? void 0 : _a.hostname) === 'localhost' || ((_b = window === null || window === void 0 ? void 0 : window.location) === null || _b === void 0 ? void 0 : _b.hostname) === '127.0.0.1');
	        if (isLocalhost) {
	            console.warn('Toolkit emit API is not set for localhost. Defaulting to localhost emit API to ensure test events are captured.');
	            return "http://localhost:3000/api";
	        }
	        else {
	            console.error("Toolkit emit API is not set. The emit API can be configured via the NEXT_PUBLIC_EVENTS_TOOLKIT_EMIT_API or " +
	                "EVENTS_TOOLKIT_EMIT_API environment variables. Falling back to staging endpoint: https://zapier-staging.com/api/v4/tracking/event/");
	            return "https://zapier-staging.com/api/v4/tracking/event/";
	        }
	    }
	}
	function getWriteToDataLayer() {
	    if (process.env.NEXT_PUBLIC_EVENTS_TOOLKIT_WRITE_TO_DATA_LAYER) {
	        // If we're running in a browser, we'll want to check for NEXT_PUBLIC_ variables
	        // https://nextjs.org/docs/basic-features/environment-variables#exposing-environment-variables-to-the-browser
	        return process.env.NEXT_PUBLIC_EVENTS_TOOLKIT_WRITE_TO_DATA_LAYER;
	    }
	    else if (process.env.EVENTS_TOOLKIT_WRITE_TO_DATA_LAYER) {
	        return process.env.EVENTS_TOOLKIT_WRITE_TO_DATA_LAYER;
	    }
	    else {
	        return "false";
	    }
	}
	EventsToolkitSettings.settings = {
	    streaming_service: process.env.EVENTS_TOOLKIT_STREAMING_SERVICE
	        ? process.env.EVENTS_TOOLKIT_STREAMING_SERVICE
	        : "api",
	    emit_api: getEventsToolkitEmitAPI(),
	    domain: new URL(getEventsToolkitEmitAPI()).hostname,
	    write_to_data_layer: getWriteToDataLayer(),
	};
	return EventsToolkitSettings;
}

var EventsToolkitUtilities = {};

var hasRequiredEventsToolkitUtilities;

function requireEventsToolkitUtilities () {
	if (hasRequiredEventsToolkitUtilities) return EventsToolkitUtilities;
	hasRequiredEventsToolkitUtilities = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.removeSessionStorageItem = exports.getSessionStorageItem = exports.setSessionStorageItem = exports.querystringToObj = exports.isInPopup = exports.isInIframe = exports.isBrowser = void 0;
		const isBrowser = () => typeof window !== "undefined";
		exports.isBrowser = isBrowser;
		const isInIframe = () => {
		    if (!(0, exports.isBrowser)())
		        return false;
		    try {
		        return window.self !== window.top;
		    }
		    catch (e) {
		        return true;
		    }
		};
		exports.isInIframe = isInIframe;
		const isInPopup = () => {
		    return (0, exports.isBrowser)() && !!window.opener;
		};
		exports.isInPopup = isInPopup;
		const querystringToObj = (query) => {
		    var _a, _b;
		    let search = (_b = query !== null && query !== void 0 ? query : (_a = window === null || window === void 0 ? void 0 : window.location) === null || _a === void 0 ? void 0 : _a.search) !== null && _b !== void 0 ? _b : "";
		    let params = new URLSearchParams(search);
		    let obj = {};
		    for (const [key, val] of params) {
		        obj[key] = val;
		        try {
		            // Check to see if the value looks like a URL so we can find search params on it
		            if (val.startsWith("https://") ||
		                val.startsWith("http://") ||
		                val.startsWith("/") ||
		                val.startsWith("?")) {
		                let nestedQueryObj = (0, exports.querystringToObj)(new URL(val, "https://fake.example").search);
		                // merge and give priority to values already on the obj
		                obj = Object.assign(Object.assign({}, nestedQueryObj), obj);
		            }
		        }
		        catch (e) {
		            // not a url, just a regular key/value pair
		            continue;
		        }
		    }
		    return obj;
		};
		exports.querystringToObj = querystringToObj;
		const setSessionStorageItem = (key, value) => {
		    if (typeof window !== 'undefined') {
		        try {
		            window.sessionStorage.setItem(key, value);
		        }
		        catch (error) {
		            console.warn('Warning: Failed to set sessionStorage item:', error);
		        }
		    }
		    else {
		        console.warn('Warning: window object is not defined.');
		    }
		};
		exports.setSessionStorageItem = setSessionStorageItem;
		const getSessionStorageItem = (key, defaultValue = "") => {
		    if (typeof window !== 'undefined') {
		        try {
		            return window.sessionStorage.getItem(key) || defaultValue;
		        }
		        catch (error) {
		            console.warn('Warning: Failed to get sessionStorage item:', error);
		            return defaultValue;
		        }
		    }
		    else {
		        return defaultValue;
		    }
		};
		exports.getSessionStorageItem = getSessionStorageItem;
		const removeSessionStorageItem = (key) => {
		    if (typeof window !== 'undefined') {
		        try {
		            window.sessionStorage.removeItem(key);
		        }
		        catch (error) {
		            console.warn('Warning: Failed to remove sessionStorage item:', error);
		        }
		    }
		    else {
		        console.warn('Warning: window object is not defined.');
		    }
		};
		exports.removeSessionStorageItem = removeSessionStorageItem; 
	} (EventsToolkitUtilities));
	return EventsToolkitUtilities;
}

var hasRequiredVisitorTracking;

function requireVisitorTracking () {
	if (hasRequiredVisitorTracking) return VisitorTracking;
	hasRequiredVisitorTracking = 1;
	Object.defineProperty(VisitorTracking, "__esModule", { value: true });
	VisitorTracking.getPageViewId = VisitorTracking.getSessionId = VisitorTracking.getVisitorId = void 0;
	const EventsToolkitSettings_1 = requireEventsToolkitSettings();
	const EventsToolkitUtilities_1 = requireEventsToolkitUtilities();
	const sessionIdCookieName = "session_id";
	const visitorIdCookieName = "visitor_id";
	const pageViewIdSessionStorageName = "page_view_id";
	function getCookie(name) {
	    var _a;
	    let matches = (_a = document.cookie) === null || _a === void 0 ? void 0 : _a.match(new RegExp("(?:^|; )" +
	        name.replace(/([\.$?*|{}\(\)\[\]\\\/\+^])/g, "\\$1") +
	        "=([^;]*)"));
	    return matches ? decodeURIComponent(matches[1]) : undefined;
	}
	// this routine is based on various approaches that have been posted and discussed
	// at https://gist.github.com/jed/982883
	// we prefer to do "inline" uuid generation here instead of including a separate library.
	const getUuid = (a = "") => {
	    var _a;
	    return a
	        ? /* eslint-disable no-bitwise */
	            (Number(a) ^
	                (((_a = window.crypto) === null || _a === void 0 ? void 0 : _a.getRandomValues(new Uint8Array(1))[0]) &
	                    (15 >> (Number(a) / 4)))).toString(16)
	        : `${1e7}-${1e3}-${4e3}-${8e3}-${1e11}`.replace(/[018]/g, getUuid);
	};
	function getVisitorId() {
	    if ((0, EventsToolkitUtilities_1.isBrowser)()) {
	        var visitorId = getCookie(visitorIdCookieName);
	        if (!visitorId) {
	            visitorId = getUuid();
	            document.cookie =
	                visitorIdCookieName +
	                    "=" +
	                    visitorId +
	                    ";domain=" +
	                    EventsToolkitSettings_1.settings.domain +
	                    ";path=/;max-age=5897232000";
	        }
	        return visitorId;
	    }
	    else {
	        return undefined;
	    }
	}
	VisitorTracking.getVisitorId = getVisitorId;
	function getSessionId() {
	    if ((0, EventsToolkitUtilities_1.isBrowser)()) {
	        var sessionId = getCookie(sessionIdCookieName);
	        if (!sessionId) {
	            sessionId = getUuid();
	        }
	        // session id values should be regenerated after 60 minutes (3600 seconds) of inactivity
	        document.cookie =
	            sessionIdCookieName +
	                "=" +
	                sessionId +
	                ";domain=" +
	                EventsToolkitSettings_1.settings.domain +
	                ";path=/;max-age=3600";
	        return sessionId;
	    }
	    else {
	        return undefined;
	    }
	}
	VisitorTracking.getSessionId = getSessionId;
	function getPageViewId() {
	    if ((0, EventsToolkitUtilities_1.isBrowser)()) {
	        var pageViewIdSession = sessionStorage.getItem(pageViewIdSessionStorageName);
	        var pageViewIdJson;
	        var pageViewId;
	        if (pageViewIdSession) {
	            pageViewIdJson = JSON.parse(pageViewIdSession);
	        }
	        if (pageViewIdJson && pageViewIdJson.length == 2 && pageViewIdJson[0] == window.location.pathname) {
	            pageViewId = pageViewIdJson[1];
	        }
	        else {
	            pageViewId = getUuid();
	            pageViewIdSession = JSON.stringify([location.pathname, pageViewId]);
	            sessionStorage.setItem(pageViewIdSessionStorageName, pageViewIdSession);
	        }
	        return pageViewId;
	    }
	    else {
	        return undefined;
	    }
	}
	VisitorTracking.getPageViewId = getPageViewId;
	return VisitorTracking;
}

var EventsToolkit = {};

var version = {};

var hasRequiredVersion;

function requireVersion () {
	if (hasRequiredVersion) return version;
	hasRequiredVersion = 1;
	Object.defineProperty(version, "__esModule", { value: true });
	version.LIB_VERSION = void 0;
	version.LIB_VERSION = "1.1.18";
	return version;
}

var hasRequiredEventsToolkit;

function requireEventsToolkit () {
	if (hasRequiredEventsToolkit) return EventsToolkit;
	hasRequiredEventsToolkit = 1;
	var __awaiter = (EventsToolkit && EventsToolkit.__awaiter) || function (thisArg, _arguments, P, generator) {
	    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
	    return new (P || (P = Promise))(function (resolve, reject) {
	        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
	        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
	        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
	        step((generator = generator.apply(thisArg, _arguments || [])).next());
	    });
	};
	Object.defineProperty(EventsToolkit, "__esModule", { value: true });
	EventsToolkit.emit = void 0;
	const EventsToolkitSettings_1 = requireEventsToolkitSettings();
	const EventsToolkitUtilities_1 = requireEventsToolkitUtilities();
	const _1 = requireDist();
	const VisitorTracking_1 = requireVisitorTracking();
	const VisitorTracking_2 = requireVisitorTracking();
	const version_1 = requireVersion();
	const PREVIOUS_LOCATION_KEY = _1.visitorId + "-previous-location";
	const NEXT_PAGE_VIEW_REFERRER_KEY = _1.visitorId + "-next-page-view-referrer";
	const PageViewEvent = "web_analytics.tracking.page_view.PageViewEvent";
	const UserInteractionEvent = "web_analytics.tracking.user_interaction.UserInteractionEvent";
	function setPreviousLocation(locationHref, query) {
	    // we use session storage here to ensure this goes away when a browser window/tab is closed
	    // but that it's also persisted if a browser session is restored.
	    let referrer = getReferrerFromParams(query);
	    let preReferer = (0, EventsToolkitUtilities_1.getSessionStorageItem)(PREVIOUS_LOCATION_KEY);
	    if (preReferer === "" && referrer !== undefined) {
	        // set referrer if there's empty cache
	        (0, EventsToolkitUtilities_1.setSessionStorageItem)(PREVIOUS_LOCATION_KEY, referrer);
	    }
	    else if ((referrer !== undefined && checkZapierDomain(referrer) && preReferer !== "") || (referrer === undefined && preReferer !== "")) {
	        // do not overwrite referrer from storage if it's zapier domain or empty referrer
	        return;
	    }
	    else if (referrer !== undefined && referrer !== preReferer && !checkZapierDomain(referrer)) {
	        //  overwrite referrer if it's different from local storage and not zapier domain
	        (0, EventsToolkitUtilities_1.setSessionStorageItem)(PREVIOUS_LOCATION_KEY, referrer);
	    }
	    else {
	        //  default to current href
	        (0, EventsToolkitUtilities_1.setSessionStorageItem)(PREVIOUS_LOCATION_KEY, locationHref);
	    }
	}
	function getPreviousLocation(query) {
	    // we use "referer" to record where folks came from when they arrive in an app.
	    // we also use it to record their previous location as they navigate around within
	    // an app.   To ensure this works properly for Single Page Apps, we keep track
	    // of this in session storage.
	    let referrer = getReferrerFromParams(query);
	    if (referrer !== undefined && !checkZapierDomain(referrer)) {
	        return referrer;
	    }
	    return (0, EventsToolkitUtilities_1.getSessionStorageItem)(PREVIOUS_LOCATION_KEY, document.referrer);
	}
	function removePreviousLocation() {
	    return (0, EventsToolkitUtilities_1.removeSessionStorageItem)(PREVIOUS_LOCATION_KEY);
	}
	function removeNextPageViewReferrer() {
	    return (0, EventsToolkitUtilities_1.removeSessionStorageItem)(NEXT_PAGE_VIEW_REFERRER_KEY);
	}
	function setNextPageViewReferrer(locationHref) {
	    return (0, EventsToolkitUtilities_1.setSessionStorageItem)(NEXT_PAGE_VIEW_REFERRER_KEY, locationHref);
	}
	function getNextPageViewReferrer() {
	    return (0, EventsToolkitUtilities_1.getSessionStorageItem)(NEXT_PAGE_VIEW_REFERRER_KEY, document.referrer);
	}
	function getReferrerFromParams(query) {
	    // As referer, referrer, from are being used in our code now so it's necessary
	    // to check all for the referer. For more detail:
	    // https://zapierorg.atlassian.net/browse/EV-1269
	    return query.referrer || query.referer || query.from || document.referrer;
	}
	function checkZapierDomain(url) {
	    const regex = /zapier(-staging)?\.com/;
	    return regex.test(url);
	}
	function applyDataGovernanceDefaults(eventData, eventType) {
	    let page_path = null;
	    let screen_size = null;
	    let iframe_flag = (0, EventsToolkitUtilities_1.isInIframe)();
	    let popup_flag = (0, EventsToolkitUtilities_1.isInPopup)();
	    let user_agent = null;
	    let url_address = null;
	    let query_string = null;
	    let fully_qualified_domain_name = null;
	    let utm_campaign = null;
	    let utm_content = null;
	    let utm_medium = null;
	    let utm_source = null;
	    let visitor_id = null;
	    let session_id = null;
	    let page_view_id = null;
	    let referer = null;
	    let events_library_version = version_1.LIB_VERSION;
	    let event_action = null;
	    let interaction_name = null;
	    let interaction_raw_text = null;
	    if ((0, EventsToolkitUtilities_1.isBrowser)()) {
	        fully_qualified_domain_name = window.location.hostname;
	        url_address = window.location.href;
	        page_path = window.location.pathname;
	        query_string = window.location.search;
	        user_agent = window.navigator.userAgent;
	        var queryStringObject = (0, EventsToolkitUtilities_1.querystringToObj)(query_string);
	        utm_campaign = queryStringObject.utm_campaign;
	        utm_content = queryStringObject.utm_content;
	        utm_medium = queryStringObject.utm_medium;
	        utm_source = queryStringObject.utm_source;
	        const width = window.innerWidth ||
	            document.documentElement.clientWidth ||
	            document.body.clientWidth;
	        const height = window.innerHeight ||
	            document.documentElement.clientHeight ||
	            document.body.clientHeight;
	        screen_size = `${width}x${height}`;
	        visitor_id = _1.visitorId;
	        session_id = (0, VisitorTracking_1.getSessionId)();
	        page_view_id = (0, VisitorTracking_2.getPageViewId)();
	        if (eventType == UserInteractionEvent) {
	            event_action = eventData.event_action || null;
	            interaction_name = eventData.interaction_name || null;
	            interaction_raw_text = eventData.interaction_raw_text || null;
	        }
	        if (eventType == PageViewEvent) {
	            // We'll save the current url_address value for the PageViewEvent
	            // as the "next PageViewEvent referrer"   This value will be used as
	            // the next PageViewEvent's referer value to ensure this is consistently
	            // tracked for Single Page Applications.
	            // As we move to different pages, we'll keep track of our history
	            // so we can use this as the "referrer" in Single Page Applications.
	            // We don't immediately make the "next PageViewEvent referrer value" the
	            // "previous location" as that can cause issues for referrer tracking
	            // on other events like the UserInteractionEvent.
	            setPreviousLocation(getNextPageViewReferrer(), queryStringObject);
	            referer = getPreviousLocation(queryStringObject);
	            setNextPageViewReferrer(url_address);
	        }
	        else {
	            referer = getPreviousLocation(queryStringObject);
	        }
	    }
	    return Object.assign({ fully_qualified_domain_name,
	        url_address,
	        query_string,
	        page_path,
	        screen_size,
	        iframe_flag,
	        popup_flag,
	        user_agent,
	        utm_content,
	        utm_medium,
	        utm_source,
	        utm_campaign,
	        visitor_id,
	        page_view_id,
	        // Include both spellings since some event schemas use referer and some use
	        // referrer
	        referer, referrer: referer, session_id,
	        events_library_version,
	        interaction_name,
	        interaction_raw_text,
	        event_action }, eventData);
	}
	function writeToDataLayer(eventType, eventData) {
	    if ((0, EventsToolkitUtilities_1.isBrowser)() && EventsToolkitSettings_1.settings.write_to_data_layer === "true") {
	        window.dataLayer = window.dataLayer || [];
	        window.dataLayer.push({
	            event: "analyticsEvent",
	            eventLabel: eventType,
	            eventAction: eventData.event_action,
	            interactionName: eventData.interaction_name,
	            interactionRawText: eventData.interaction_raw_text,
	            pagePath: eventData.page_path,
	            visitorId: _1.visitorId,
	        });
	    }
	}
	if (typeof window !== 'undefined') {
	    window.addEventListener("beforeunload", () => {
	        removePreviousLocation();
	        removeNextPageViewReferrer();
	    });
	}
	function emit(eventData, eventType) {
	    return __awaiter(this, void 0, void 0, function* () {
	        let emitStatus = true;
	        let eventRequestData = undefined;
	        try {
	            eventRequestData = applyDataGovernanceDefaults(eventData, eventType);
	            const response = yield fetch(EventsToolkitSettings_1.settings.emit_api, {
	                body: JSON.stringify({
	                    properties: eventRequestData,
	                    subject: eventType,
	                }),
	                credentials: "include",
	                headers: {
	                    "Content-Type": "application/json",
	                    "X-Requested-With": "XMLHttpRequest",
	                    Accept: "application/json",
	                },
	                method: "POST",
	                // Allow the request to outlive the page context it was created from.
	                // Without this flag, events fired shortly before a full page navigation
	                // will be cancelled by the browser if the request doesn't finish before
	                // the page is shut down. This results in lost events.
	                keepalive: true,
	            });
	            if (response.status !== 200) {
	                console.warn("Unable to emit event:", response);
	                emitStatus = false;
	            }
	            else {
	                writeToDataLayer(eventType, eventRequestData);
	            }
	        }
	        catch (error) {
	            console.warn("Unable to emit event:", error);
	            emitStatus = false;
	        }
	        return [emitStatus, eventRequestData];
	    });
	}
	EventsToolkit.emit = emit;
	return EventsToolkit;
}

var hasRequiredDist;

function requireDist () {
	if (hasRequiredDist) return dist;
	hasRequiredDist = 1;
	(function (exports) {
		Object.defineProperty(exports, "__esModule", { value: true });
		exports.emit = exports.visitorId = void 0;
		const VisitorTracking_1 = requireVisitorTracking();
		exports.visitorId = (0, VisitorTracking_1.getVisitorId)();
		const EventsToolkit_1 = requireEventsToolkit();
		Object.defineProperty(exports, "emit", { enumerable: true, get: function () { return EventsToolkit_1.emit; } });
		globalThis.EventToolkit = { emit: EventsToolkit_1.emit }; 
	} (dist));
	return dist;
}

var distExports = requireDist();

const trimStr = (str, length) => {
  if (typeof str !== "string") return str;
  if (str.length <= length) return str;
  const half = Math.floor(length / 2);
  return str.slice(0, half) + " [TRUNCATED]" + str.slice(-half);
};
const recurseTrim = (obj, length) => {
  if (typeof obj === "string") {
    return trimStr(obj, length);
  }
  if (Array.isArray(obj)) {
    return obj.map((item) => recurseTrim(item, length));
  }
  if (obj && typeof obj === "object" && obj !== null) {
    const result = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        result[key] = recurseTrim(obj[key], length);
      }
    }
    return result;
  }
  return obj;
};

const emitEventFromServer = async (eventData, eventType) => {
  if (process.env.NODE_ENV === "development") {
    console.info(`🧰 Would have tracked: ${eventType}`, eventData);
    return [false, void 0];
  }
  const url = `${process.env.NEXT_PUBLIC_ZAPIER_ORIGIN ?? "https://zapier.com"}/api/v4/tracking/event/`;
  try {
    await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        subject: eventType,
        properties: eventData
      })
    });
    return [true, void 0];
  } catch (e) {
    console.error(`Error sending ${eventType}`, e);
    return [false, void 0];
  }
};
const emitEventFromClient = async (eventData, eventType) => {
  if (process.env.NODE_ENV === "development") {
    console.info(`🧰 Would have tracked: ${eventType}`, eventData);
    return [false, void 0];
  }
  return distExports.emit(eventData, eventType);
};
const trackCopilotToolExecutedAttemptEvent = (eventData) => {
  emitEventFromServer(eventData, "ai.tracking.CopilotToolExecutedAttemptEvent");
};
const trackCopilotPredictionOutputEvent = (eventData) => {
  emitEventFromServer(eventData, "ai.tracking.CopilotPredictionOutputEvent");
};
const trackCopilotInteractionEvent = (eventData) => {
  emitEventFromClient(eventData, "ai.tracking.CopilotInteractionEvent");
};
const getCommonTrackingEventFields = ({
  accountId,
  conversationId,
  copilotId,
  customUserId,
  userMessageId
}) => {
  return {
    event_id: crypto.randomUUID(),
    timestamp_ms: Date.now(),
    copilot_id: copilotId,
    copilot_thread_id: conversationId ?? "",
    cross_product_conversation_id: conversationId ?? "",
    asset_type: copilotId === "editor" ? "zap" : void 0,
    user_message_id: userMessageId,
    customuser_id: customUserId,
    account_id: accountId
  };
};

export { HANDOFF_TOOL_NAME as H, makeFinalTool as a, makeServerTool as b, makeToolComponent as c, createOnToolCall as d, extractComponents as e, createErrorObject as f, createServerTools as g, commonToolParams as h, createCopilotRegistry as i, globalCopilots as j, getGlobalCopilotRegistry as k, trackCopilotPredictionOutputEvent as l, makeClientTool as m, trackCopilotInteractionEvent as n, getCommonTrackingEventFields as o, getSemanticSearchOrigin as p, getZapierOrigin as q, recurseTrim as r, serializeTools as s, trackCopilotToolExecutedAttemptEvent as t, getCustomActionsOrigin as u, HandoffStatus as v };
//# sourceMappingURL=utils-B26sQ2ML.mjs.map
